// A column's head that sorts by it (B-366, B-377): a press sorts, pressed
// again turns the order round; resting on it, a long press or the keyboard's
// focus says what the column is (B-374). The sorted head is in the accent with
// an arrow, so the order reads without the menu, and without color.
//
// The Players page drew its own until 2026-10-17; this is the one every
// table with sortable heads shares (Players, Standings, the draft room), so a
// head sorts the same way on every screen.

import { useState } from 'react';
import { Term } from './Why';

/** Which way a column's own first press sorts: its highest first, or lowest. */
export type SortFirst = 'high' | 'low';

export function SortHead({
  label,
  tip,
  on,
  reverse,
  first = 'high',
  onSort,
  name,
}: {
  label: string;
  /** What the column means, said when asked. */
  tip: string;
  /** Whether the table is in this column's order. */
  on: boolean;
  /** Turned round from its first press. */
  reverse: boolean;
  first?: SortFirst;
  onSort: () => void;
  /** Its accessible name's first words, when the label is a symbol. */
  name?: string;
}) {
  const low = (first === 'low') !== reverse;
  return (
    <Term
      tip={tip}
      name={name ?? label}
      className={`fsp-sorthead${on ? ' is-on' : ''}`}
      onActivate={onSort}
      state={
        on
          ? low
            ? 'Sorted lowest first; press for highest first.'
            : 'Sorted highest first; press for lowest first.'
          : 'Press to sort by it.'
      }
    >
      {label}
      {on ? (
        <span className="fsp-sorthead-arrow" aria-hidden="true">
          {low ? '▲' : '▼'}
        </span>
      ) : null}
    </Term>
  );
}

/**
 * A table's order: which column, and whether it is turned round. A press on
 * another column sorts by it its own way first; on the same one, turns it
 * round. `null` is the table's own order.
 */
export function useSort<K extends string>(
  initial: K | null = null,
): {
  by: K | null;
  reverse: boolean;
  press: (key: K) => void;
  reset: () => void;
} {
  const [state, setState] = useState<{ by: K | null; reverse: boolean }>({
    by: initial,
    reverse: false,
  });
  return {
    by: state.by,
    reverse: state.reverse,
    press: (key) =>
      setState((s) =>
        s.by === key ? { by: key, reverse: !s.reverse } : { by: key, reverse: false },
      ),
    reset: () => setState({ by: null, reverse: false }),
  };
}

/**
 * Sort rows by a figure, its first press's way first, nulls last either way.
 * Ties keep the table's own order (a stable sort over the rows as served).
 */
export function sortRows<R>(
  rows: readonly R[],
  value: ((r: R) => number | null) | null,
  first: SortFirst,
  reverse: boolean,
): R[] {
  if (value === null) return [...rows];
  const low = (first === 'low') !== reverse;
  return rows
    .map((r, i) => ({ r, i, v: value(r) }))
    .sort((a, b) => {
      if (a.v === null || b.v === null) return a.v === b.v ? a.i - b.i : a.v === null ? 1 : -1;
      return (low ? a.v - b.v : b.v - a.v) || a.i - b.i;
    })
    .map((x) => x.r);
}
